// THE STORED ROW — the lifecycle invariant that makes a self-contradicting row
// unreadable, the columns themselves, the draft an inlet files, and the two records a
// settle is read through.
// Split out of `../raw-task.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { RawTaskAuthorTrustSchema } from '../../value-objects/author-trust';
import { AccountIdSchema, RawTaskIdSchema, TaskIdSchema } from '../../value-objects/ids';
import {
  RepoFullNameSchema,
  TaskLlmPreferenceSchema,
  TextProvenanceSchema,
} from '../../value-objects/task-envelope';
import { AuditEventKind } from '../audit';
import { RawTaskUrgencySchema } from '../security-watch';
import {
  ErrorSignatureSchema,
  RAW_TASK_AFTER_TASKS_MAX,
  RAW_TASK_ASK_MAX,
  RAW_TASK_DEDUPE_KEY_MAX,
  RAW_TASK_LEASE_MS,
  RAW_TASK_MAX_ATTEMPTS,
  RAW_TASK_REPOS_MAX,
  RAW_TASK_REQUIRED_ACCESS_ITEM_MAX,
  RAW_TASK_REQUIRED_ACCESS_MAX,
  RawTaskEvidenceSchema,
  RawTaskOriginRefSchema,
} from './evidence';
import { RawTaskIssueSchema } from './issue';
import {
  RawTaskSourceSchema,
  RawTaskStatus,
  RawTaskStatusSchema,
  rawTaskStatusForVerdict,
  type SettledRawTaskStatus,
} from './status';
import { type RawTaskVerdict, RawTaskVerdictSchema } from './verdict';

/**
 * The lifecycle invariant: `status`, `verdict`, `settled_at` and
 * `lease_expires_at` are four spellings of ONE position in the admission
 * lifecycle, and exactly six combinations of them exist.
 *
 * | status | verdict | settled_at | lease_expires_at | means |
 * |---|---|---|---|---|
 * | `pending`   | null | null | null | filed, nothing has looked at it |
 * | `triaged`   | null | null | SET  | claimed, in flight |
 * | `triaged`   | `schedule` | SET | null | admitted |
 * | `dropped`   | `drop-noise` \| `dupe-of` | SET | null | decided not to happen |
 * | `escalated` | `escalate` | SET | null | a human decides |
 * | `awaiting_info` | `needs_info` | SET | null | the asker owes an answer (and `infoRounds >= 1`) |
 *
 * Everything else is a row that contradicts itself — a `pending` ask carrying a
 * decision, a `dropped` one carrying none, an in-flight row with no lease to
 * expire. The prose above used to be the only place this was written down, which
 * makes it a comment asserting a guarantee the code did not provide.
 *
 * WHAT IS NOT HERE, and deliberately: "a `schedule` names the work it minted".
 * That one is STRUCTURAL, one union member up — `task_ids` is a required,
 * non-empty field of the `schedule` member of {@link RawTaskVerdictSchema}, so a
 * verdict without it never reaches this function to be refused. A refinement
 * would have been the weaker place for it: it fires at parse, while the union
 * fires at compile.
 *
 * Enforced HERE at the read boundary and by a `CHECK` on the table at the write
 * boundary, deliberately both: the constraint is what makes a bad row
 * unwritable, and this is what makes one already written unreadable rather than
 * quietly acted on. The verdict↔status agreement is the same fact
 * {@link rawTaskStatusForVerdict} derives, so a settle cannot produce a row this
 * rejects.
 */
function lifecycleRefinement(
  row: {
    status: RawTaskStatus;
    verdict: RawTaskVerdict | null;
    settledAt: Date | null;
    leaseExpiresAt: Date | null;
    infoRounds: number;
    hold: RawTaskHold | null;
  },
  ctx: z.RefinementCtx,
): void {
  const refuse = (message: string, path: string): void => {
    ctx.addIssue({ code: 'custom', message, path: [path] });
  };
  const inFlight = row.status === RawTaskStatus.Triaged && row.verdict === null;

  if (row.status === RawTaskStatus.Pending || inFlight) {
    if (row.verdict !== null) refuse(`a ${row.status} raw task carries no verdict`, 'verdict');
    if (row.settledAt !== null) refuse(`a ${row.status} raw task is not settled`, 'settledAt');
  } else {
    if (row.verdict === null) refuse(`a ${row.status} raw task must carry its verdict`, 'verdict');
    if (row.settledAt === null) refuse(`a ${row.status} raw task must be settled`, 'settledAt');
    // The verdict and the status are one decision; derive rather than compare
    // two hand-written lists, so a fifth verdict cannot disagree with a fifth
    // status.
    if (row.verdict !== null && rawTaskStatusForVerdict(row.verdict.verdict) !== row.status) {
      refuse(
        `verdict '${row.verdict.verdict}' settles to '${rawTaskStatusForVerdict(row.verdict.verdict)}', not '${row.status}'`,
        'status',
      );
    }
  }

  // The lease is non-null on EXACTLY the in-flight row: it is what makes that
  // state expire, and a settled or unclaimed row holding one would be a claim
  // nobody is serving.
  if (inFlight && row.leaseExpiresAt === null) {
    refuse('a claimed raw task must carry the lease that bounds the claim', 'leaseExpiresAt');
  }
  if (!inFlight && row.leaseExpiresAt !== null) {
    refuse(`a ${row.status} raw task holds no lease`, 'leaseExpiresAt');
  }

  // A hold window parks an UNCLAIMED ask (`raw_tasks_hold_until_ck`): the claim writes it
  // NULL, so a held row in any other state is one the claim never cleared.
  if (row.hold !== null && row.status !== RawTaskStatus.Pending) {
    refuse(`a ${row.status} raw task holds no hold window`, 'hold');
  }

  // A parked ask has asked at least once: the settle that writes `awaiting_info`
  // counts the round it asked in the same update, so a zero here is a question
  // the round cap never saw — the endless loop `RAW_TASK_INFO_ROUNDS_MAX` bounds.
  if (row.status === RawTaskStatus.AwaitingInfo && row.infoRounds < 1) {
    refuse('an awaiting_info raw task must count the round it asked', 'infoRounds');
  }
}

// ─── the row ─────────────────────────────────────────────────────────────────

/**
 * Why the claim is SKIPPING an ask — the closed vocabulary `raw_tasks_hold_reason_ck`
 * admits, one member per hold door (`packages/db` `queries/raw-tasks-hold.ts`, whose
 * hold-reason list this is pinned equal to by `screen-verdicts-schema.test.ts`):
 * the four tenant gates, the judge (`model_unavailable`), a human's open PR
 * (`human_in_flight`), the intake screen failing closed (`screen_unavailable`), a
 * full judge key (`key_capacity_full`), a cooling one (`key_cooling`, 0342) and the
 * account's queue past its backlog horizon (`intake_saturated`, 0350 — the shedder's
 * hold, `RAW_TASK_INTAKE_SATURATED_REASON` in `./redrive`) and the repo's discretionary
 * queue past its budget (`queue_over_budget`, #7105: the queue controller's throttle —
 * the attempt is REFUNDED, unlike `intake_saturated`, and the controller wakes the hold as
 * headroom frees. Reader this release, writer next: the stored closed-set rule,
 * `docs/standards/stored-closed-sets.md`) and triage's re-check defer (`triage_recheck`, 0421:
 * judged, deferred on a reason that is not a person, woken only by its window — the reader
 * shipped first (#7923), its writer, triage's defer, behind it by the same rule).
 */
export const RawTaskHoldReasonSchema = z.enum([
  'billing_paused',
  'no_byok',
  'model_parked',
  'key_inactive',
  'model_unavailable',
  'human_in_flight',
  'screen_unavailable',
  'key_capacity_full',
  'key_cooling',
  'intake_saturated',
  'queue_over_budget',
  'triage_recheck',
]);

/**
 * A held ask's WINDOW (#6501 A3): the claim skips the row until `until`, and the re-check
 * re-decides it after. Present exactly while `raw_tasks.hold_until` is set — a `pending`
 * row only (`raw_tasks_hold_until_ck`); an `until` in the past is a re-check that is DUE,
 * not one that ran. Without it a held ask read exactly like a fresh one.
 *
 * The STORED `hold_reason` outlives the window on purpose (the next hold compares against
 * it, `firstHold`), so it is surfaced only beside a window: a reason with no window
 * describes a hold that already ended.
 */
export const RawTaskHoldSchema = z.object({
  reason: RawTaskHoldReasonSchema,
  until: z.date(),
});
export type RawTaskHold = z.infer<typeof RawTaskHoldSchema>;

/** The stored columns, without the lifecycle invariant — see {@link RawTaskSchema}. */
/** The envelope's own shape, so what a re-file carries reaches the mint unchanged. */
export const RawTaskTextProvenanceSchema = TextProvenanceSchema;
export type RawTaskTextProvenance = z.infer<typeof RawTaskTextProvenanceSchema>;

const RawTaskFieldsSchema = z.object({
  /** `rtk_<hex>` — the branded id IS the stored text PK (the `tsk_` scheme). */
  id: RawTaskIdSchema,
  accountId: AccountIdSchema,
  source: RawTaskSourceSchema,
  /** PLURAL. May be empty: a devops or platform ask need name no repo at all. */
  repoFullNames: z.array(RepoFullNameSchema).max(RAW_TASK_REPOS_MAX),
  /** The intent, in prose. What should be true — not how to make it true. */
  ask: z.string().min(1).max(RAW_TASK_ASK_MAX),
  evidence: RawTaskEvidenceSchema,
  /**
   * The scout's one implementation-adjacent finding, and it belongs here rather
   * than on `tasks`: whether the worker, the reviewer and CI will have the keys,
   * MCP servers and secrets this work needs is knowable at PLAN time and decides
   * whether the work can run at all (`docs/idea/triage.md`). It is the same
   * question `ScoutBlockedProposal` answers today, made durable instead of
   * terminal.
   */
  requiredAccess: z
    .array(z.string().min(1).max(RAW_TASK_REQUIRED_ACCESS_ITEM_MAX))
    .max(RAW_TASK_REQUIRED_ACCESS_MAX),
  /**
   * EXISTING tasks (`tsk_…`) every piece of this ask must wait behind — the one
   * ORDERING fact a caller may state, because it is about work that already
   * exists rather than about the live fleet. Triage never writes it and cannot
   * drop it: the mint appends these ids to the `depends_on` of every ROOT task the
   * ask's plan cuts (the rest wait transitively), so the claim gate holds the whole
   * ask until each one merges (or finishes with no PR). Validated at the door that
   * accepted it — same account, not dead. Empty for every machine inlet.
   *
   * `.default([])` for the migration's reason, like `attempts`: `0260` adds the
   * column with that default and a projection written before it carries none.
   */
  afterTaskIds: z.array(TaskIdSchema).max(RAW_TASK_AFTER_TASKS_MAX).default([]),
  originRef: RawTaskOriginRefSchema,
  /**
   * THE GitHub issue this ask is ABOUT — `{repo, number}` (see
   * {@link RawTaskIssueSchema}), stated by the CALLER, never read out of the
   * ask's prose: an ask that merely MENTIONS an issue links nothing.
   *
   * It is what decides which of the plan's pieces may carry
   * `envelope.issue_number`, and therefore which pull request closes the issue
   * when it merges (`rawTaskIssueLink`, `./issue`). The number is a one-way
   * door — `Closes #n` closes a real issue — so it is a field a caller stated,
   * with the issue's REPOSITORY beside it, and never a number parsed out of
   * text.
   *
   * NULLABLE, and NULL is the one spelling of "this ask names no issue": the
   * row is REQUIRED to carry the field, so a reader never has to ask whether
   * an absent key means "no issue" or "a row written before the column
   * existed". `0380` adds the column with no backfill — every row filed
   * before it names no issue, and NULL says exactly that — and the parse
   * fills the absent key with it, so a projection or a fixture written
   * before the column existed still reads as a row that names no issue.
   */
  issue: RawTaskIssueSchema.nullable().default(null),
  /**
   * Who wrote this ask's text — see {@link RawTaskAuthorTrustSchema}. Stamped by the
   * door that filed it, never rewritten (a redelivery keeps the first filer's stamp,
   * like `filedByUserId`). REQUIRED on the row and on the draft, no default: migration
   * `0286` backfilled every pre-existing row by `source`, so a projection without it is
   * a drift this parse refuses, and a door that forgets it fails to compile.
   */
  authorTrust: RawTaskAuthorTrustSchema,
  /**
   * WHOSE WORDS this ask quotes, when that is not its filer (`raw_tasks.text_provenance`, #7785
   * part 2): a machine re-file of an outsider's task keeps it (`refileTextProvenance`), so the
   * `platform` stamp above cannot launder their text. NULL on every other row: its door's trust
   * and source answer. First filer wins, like `authorTrust`.
   */
  textProvenance: RawTaskTextProvenanceSchema.nullable().default(null),
  /**
   * The PERSON who filed this ask (`users.id`, 0252) — stamped at the three human
   * doors only (`ask file`, `POST /v1/asks`, the multi-repo `plan_start`); NULL
   * for every machine inlet, for an unbound-PAT ask, and for every row filed
   * before the column existed. Provenance of the WRITE: the first filer wins, a
   * redelivery never rewrites it, `set null` once that person is offboarded.
   *
   * REQUIRED and explicitly nullable, never optional — the `decidedByUserId`
   * posture (`entities/access-request.ts`). Read by `findTaskCoAuthor` for
   * commit credit and by `askPlanInitiatorOf` (`value-objects/plan-projection`)
   * to stamp the plan triage cuts from this ask (#5103).
   */
  filedByUserId: z.string().uuid().nullable(),
  /**
   * Unique per account — this is what makes every inlet's write idempotent, so a
   * redelivery writes zero new rows. Derived from the SOURCE's stable id for the
   * thing (a scout run + plan-local id, a Sentry short id, an issue number), never
   * from the delivery.
   */
  dedupeKey: z.string().min(1).max(RAW_TASK_DEDUPE_KEY_MAX),
  status: RawTaskStatusSchema,
  /** The decision that settled it, verbatim. NULL while `pending`, and while claimed. */
  verdict: RawTaskVerdictSchema.nullable(),
  settledAt: z.date().nullable(),
  /**
   * When this claim stops being exclusive. NON-NULL exactly while the row is
   * IN FLIGHT (`triaged` with a null verdict) and NULL in every other state —
   * see {@link RAW_TASK_LEASE_MS} for why the column exists and how long it runs.
   *
   * It doubles as the FENCE. The claim hands its value back, and `settleRawTask`
   * CASes on it: a pass whose lease expired and whose row was re-claimed by
   * someone else cannot land its settle on top of the new holder's, because the
   * value it is holding is no longer the value in the row. Without that, lease
   * expiry would convert one leak into two passes writing one decision.
   */
  leaseExpiresAt: z.date().nullable(),
  /**
   * How many times the claim has handed this ask out — see
   * {@link RAW_TASK_MAX_ATTEMPTS} for why it is bounded and where.
   *
   * `.default(0)` rather than required, for the migration rather than for
   * convenience: `0185_raw_tasks_attempts` backfills every existing row to 0 and a
   * projection written before the column existed carries none, so a parse that
   * demanded it would reject rows the table happily holds.
   */
  attempts: z.number().int().nonnegative().default(0),
  /**
   * How many `needs_info` rounds this ask has spent — see
   * `RAW_TASK_INFO_ROUNDS_MAX` (`./verdict`). Incremented by the settle that writes
   * `awaiting_info`, and NOT reset by the answer that re-opens the ask (unlike
   * `attempts`): it counts questions asked across the ask's whole life, which is
   * what makes the cap a bound on the loop rather than on one pass of it.
   * `.default(0)` for the same migration reason as `attempts`.
   */
  infoRounds: z.number().int().nonnegative().default(0),
  /**
   * The `byok_keys.id` values (uuids — the stored column is `uuid[]`, and the
   * domain's `byk_…` brand is a cast over it, `queries/byok/row.ts`) whose judge
   * calls have FAILED this ask (#5111) — the memory a `model_unavailable` hold
   * leaves on the row, so the re-check can avoid the key that just failed it.
   * Appended by the hold's model arm, cleared by EVERY settle: a judged ask
   * forgets, and the next one on this row starts from none. Required rather than
   * defaulted: `normalizeRawTask` always supplies it off a NOT NULL column, and a
   * projection without it is a drift this parse refuses.
   */
  judgeFailedKeyIds: z.array(z.uuid()),
  /**
   * The per-ask key PIN (#5117) — the `TaskLlmPreference` shape `envelope.llm`
   * already carries. NULL means no pin; the triage key pick reads it as a
   * RANKING BOOST (the same `preferenceKeyLabel` in `llm-credential/scoring.ts`),
   * never a fence — a cooled, parked or fenced pin simply loses its boost and the
   * pick ranks on without it. TWO writers: the key-outage pick, which stamps every
   * held ask of one outage (#5118), and the blocker ANSWER door, on each of the three
   * arms that leave an ask a judge reads — filed, reopened and held (#6521). Every
   * other writer leaves it null. Nullable with `.default(null)` so a projection that omits
   * the field reads as "no pin" — the field's own absence, with no
   * comparison to another column's posture.
   */
  llmPin: TaskLlmPreferenceSchema.nullable().default(null),
  /**
   * The error delivery's exception class, crash-site frame and group id (#6223),
   * written by the Sentry inlet only; NULL for every other source and for every row
   * filed before the column existed. `.default(null)` as `llmPin`, so a projection
   * or fixture that omits it reads as "no signature".
   */
  errorSignature: ErrorSignatureSchema.nullable().default(null),
  /**
   * The plan a REPAIR ask extends (`raw_tasks.extends_plan_id`, plan 101 D7, slice 10 PR 8b):
   * set only by the repair filer, NULL for every other ask and every row filed before the
   * column existed. Triage reads it: an ask carrying it appends its legs to that plan
   * (`@developerz/db` `appendPlanRepairLegs`) instead of recording a new one, so a repaired
   * goal stays one goal. `.default(null)` as `llmPin`: a projection that omits it is no repair.
   */
  extendsPlanId: z.string().min(1).nullable().default(null),
  /**
   * The live hold window, or null — see {@link RawTaskHoldSchema}. `.default(null)` as
   * `llmPin`: a projection or fixture that omits it reads as "not held".
   */
  hold: RawTaskHoldSchema.nullable().default(null),
  /**
   * The ask's place in the admission queue — see `RawTaskUrgency` (`../security-watch`).
   * `.default('routine')` as `llmPin`: a projection or fixture that omits it is an ordinary ask.
   */
  urgency: RawTaskUrgencySchema.default('routine'),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * A raw task as stored. Parsed on the way OUT of `@developerz/db`, so a
 * wire-contract drift — or a row that contradicts itself — fails loudly at the
 * read rather than three layers later. `.superRefine`
 * returns a `ZodEffects`, which has no `.pick`, so the field object is kept
 * separately above for {@link RawTaskDraftSchema} to carve from — one field
 * list, two schemas, no chance of the draft drifting from the row.
 */
export const RawTaskSchema = RawTaskFieldsSchema.superRefine(lifecycleRefinement);
export type RawTask = z.infer<typeof RawTaskSchema>;

/**
 * What an INLET hands `createRawTask` — the row minus everything the admission
 * gate owns (`id`, `status`, `verdict`, `settledAt`, timestamps). An inlet that
 * could set `status` could file work already admitted, which is the one thing
 * this object exists to prevent.
 */
export const RawTaskDraftSchema = RawTaskFieldsSchema.pick({
  accountId: true,
  source: true,
  repoFullNames: true,
  ask: true,
  evidence: true,
  requiredAccess: true,
  originRef: true,
  dedupeKey: true,
  authorTrust: true,
}).extend({
  /**
   * OPTIONAL on the draft, absent meaning none: only the caller-facing door
   * (`ask file`) can name work to wait behind, and a machine inlet has no such
   * fact to state — so it omits the field rather than every inlet writing `[]`.
   */
  afterTaskIds: z.array(TaskIdSchema).max(RAW_TASK_AFTER_TASKS_MAX).optional(),
  /** OPTIONAL, absent meaning none: only the Sentry inlet states one (#6223). */
  errorSignature: ErrorSignatureSchema.optional(),
  /**
   * OPTIONAL, absent meaning none: only the plan-repair filer states one (plan 101 D7) — the
   * plan the ask's legs append to. `createRawTask` refuses a plan id the draft's account
   * does not own.
   */
  extendsPlanId: z.string().min(1).optional(),
  /**
   * OPTIONAL, absent meaning none: only the CALLER-facing door (`ask file`,
   * `POST /v1/asks`) can name the issue an ask is about, and a machine inlet
   * has no such fact to state. The row SPELLS "no issue" as `null` (it is
   * required to carry the field); the draft SPELLS it as OMISSION, which is
   * the `needs` posture every other optional draft field takes — and only the
   * draft's says "this inlet named none".
   *
   * The SHAPE is the row's own (`RawTaskIssueSchema`), so the two cannot
   * drift; only the presence differs, and that difference is this one.
   */
  issue: RawTaskIssueSchema.nullable().optional(),
  /**
   * OPTIONAL, absent meaning the account's own route decides: the model the TRIAGE
   * JUDGE reads this ask on (#6521). Only the blocker ANSWER door states one, from a
   * person's pick, validated against the account's live pool before it is stored
   * (`keyPinVerdict`, `entities/key-outage.ts` — the key-outage pick's own verdict).
   * It reaches the judge and stops there — the tasks triage mints
   * carry no `envelope.llm` (#5117's epic decision).
   *
   * OPTIONAL and not nullable, unlike the row's own column: the ROW spells "no pin" as
   * `null` and a draft spells it as OMISSION, which is the `needs` posture on the
   * blocker draft and for its reason — two spellings of one absence, and only the
   * draft's says "this inlet named none".
   */
  llmPin: TaskLlmPreferenceSchema.optional(),
  /** OPTIONAL, absent meaning none: only a machine re-file of external text states one. */
  textProvenance: RawTaskTextProvenanceSchema.optional(),
  /** OPTIONAL, absent meaning `routine`: only the security watch states one, off a KEV row. */
  urgency: RawTaskUrgencySchema.optional(),
});
export type RawTaskDraft = z.infer<typeof RawTaskDraftSchema>;

/**
 * The audit kind a settle writes, one per terminal status. EXHAUSTIVE BY
 * CONSTRUCTION: `Record<SettledRawTaskStatus, …>` fails to compile the moment
 * `RawTaskStatus` grows a fifth member, so a status can never ship with no row
 * to announce it. "If it isn't logged, it didn't happen" is then enforced by the
 * type system rather than remembered at three write sites.
 */
export const RAW_TASK_SETTLE_AUDIT_KIND: Record<SettledRawTaskStatus, AuditEventKind> = {
  [RawTaskStatus.Triaged]: AuditEventKind.RawTaskTriaged,
  [RawTaskStatus.Dropped]: AuditEventKind.RawTaskDropped,
  [RawTaskStatus.Escalated]: AuditEventKind.RawTaskEscalated,
  [RawTaskStatus.AwaitingInfo]: AuditEventKind.RawTaskAwaitingInfo,
};

/**
 * Whether the row announcing a settle is MAINTAINER-VISIBLE, one per terminal
 * status.
 *
 * The rule is the one `entities/audit.ts` already states twice for other lanes —
 * "an `escalate` hop is `visibleToMaintainer` (a human must act); a routine
 * advance is not" (`BabysitTransition`), and "this row is the entire surface, so
 * it is `visibleToMaintainer`" (`ReviewDepthUnservable`). {@link RawTaskStatus}
 * defines `escalated` as "a human decides. Not a failure; a first-class
 * outcome", and `listRecentAuditByAccount` (`@developerz/db`) filters
 * `visible_to_maintainer` IN by default — so a first-class "a human decides"
 * outcome written `false` is an outcome no human is shown. Prod on 2026-08-28
 * held 7 `raw_task.escalated` rows, 7 of them invisible, against 1,290
 * `escalation.fired` rows all visible: the whole live population of this lane,
 * hidden by the one flag that decides who sees it.
 *
 * `triaged` and `dropped` stay internal and that is not symmetry-for-its-own-sake:
 * a `schedule` announces itself through the WORK it minted (`task_ids`, joinable
 * by `tasks.raw_task_id`), and a `drop-noise`/`dupe-of` is the lane doing its job
 * — neither asks anything of a human, and a feed that surfaced them would bury
 * the one row that does.
 *
 * A `Record<SettledRawTaskStatus, boolean>` for the same reason
 * {@link RAW_TASK_SETTLE_AUDIT_KIND} beside it is one: it fails to COMPILE the
 * moment `RawTaskStatus` grows a fifth member, so a status cannot ship without
 * someone saying out loud whether a human owes it a read. A boolean literal at
 * each write site — which is what this replaces at both of them — says nothing
 * out loud and drifts silently.
 */
export const RAW_TASK_SETTLE_VISIBLE_TO_MAINTAINER: Record<SettledRawTaskStatus, boolean> = {
  [RawTaskStatus.Triaged]: false,
  [RawTaskStatus.Dropped]: false,
  [RawTaskStatus.Escalated]: true,
  // The asker owes an answer, and the question is the maintainer's whole view of
  // the wait — the reason `escalated` is visible applies unchanged.
  [RawTaskStatus.AwaitingInfo]: true,
};
